import { DevTool } from '@hookform/devtools';
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { W_WorkOrder_FindAvailableForAttachPreviousFRTs_VM_Row } from 'ab-api/web/workOrder/findAvailableForAttachPreviousFRTs';

import type * as FieldReportSyncVMs from '@acceligentllc/shared/dtos/socket/view/newFieldReport/sync';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import socket from 'af-utils/socket.util';

import SocketEvent from 'ab-enums/socketEvent.enum';

import { Button, SearchInput } from '@acceligentllc/storybook';

import ConfirmationModal from 'af-components/ConfirmationModal';
import Checkbox from 'af-components/Controls/Checkbox';
import CustomModal from 'af-components/CustomModal';
import LabelWithColor from 'af-components/LabelWithColor';
import LoadingIndicator from 'af-components/LoadingIndicator';
import RHFCheckbox from 'af-components/RHFFieldWrappers/Checkbox/RHFCheckbox';
import Tooltip from 'af-components/Tooltip';

import * as FieldReportActions from 'af-actions/fieldReport';
import * as WorkOrderActions from 'af-actions/workOrder';

import styles from './styles.module.scss';

interface OwnProps {
	showModal: boolean;
	closeModal: () => void;
	jobId: number;
	fieldReportTypeId: number;
	workOrderId: number;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

interface FormValues {
	workOrders: Record<string, boolean>;
}

const filterBy = ['dueDate', 'workOrderCode', 'crewTypeName'];

const NUMBER_OF_DAYS_AROUND_TODAY = 2;

const SelectWorkOrdersModal: React.FC<Props> = (props) => {
	const {
		closeModal,
		showModal,
		fieldReportTypeId,
		findAllForAttachPreviousFRs,
		attachFieldReportTypePDF,
		jobId,
		workOrderId,
	} = props;

	const [showDiscardModalData, setShowDiscardModalData] = React.useState(false);
	const [isLoading, setIsLoading] = React.useState(false);
	const [filterText, setFilterText] = React.useState<string>('');
	const [availableWorkOrders, setAvailableWorkOrders] = React.useState<W_WorkOrder_FindAvailableForAttachPreviousFRTs_VM_Row[]>([]);

	const twoDaysFromNowDate = React.useMemo(() => TimeUtils.getDateNDaysFromToday(NUMBER_OF_DAYS_AROUND_TODAY, TimeFormat.DATE_ONLY_SLASHES), []);
	const twoDaysBeforeNowDate = React.useMemo(() => TimeUtils.getDateNDaysFromToday(-NUMBER_OF_DAYS_AROUND_TODAY, TimeFormat.DATE_ONLY_SLASHES), []);

	const { control, getValues, setValue } = useForm<FormValues>(
		{
			defaultValues: {
				workOrders: availableWorkOrders.reduce<FormValues['workOrders']>(
					(acc, wo) => {
						if (!wo.isAlreadyAttached) {
							return { ...acc, [wo.id]: false };
						} else {
							return { ...acc };
						}
					},
					{}
				),
			},
		}
	);

	const workOrderWatchedValues = useWatch({ control, name: 'workOrders' });

	const closeDiscardModal = React.useCallback(() => setShowDiscardModalData(false), []);

	const loadWorkOrders = React.useCallback(async () => {
		setIsLoading(true);
		const workOrders = await findAllForAttachPreviousFRs(jobId, workOrderId, fieldReportTypeId);
		setAvailableWorkOrders(workOrders.workOrders);
		setIsLoading(false);
	}, [fieldReportTypeId, findAllForAttachPreviousFRs, jobId, workOrderId]);

	React.useEffect(() => {
		loadWorkOrders();
	}, [loadWorkOrders]);

	const renderWorkOrderDetails = React.useCallback((wo: W_WorkOrder_FindAvailableForAttachPreviousFRTs_VM_Row) => {
		return (
			<span className={`${styles['report-type-row']}`}>
				<span className={`${styles['report-type-row__small-text']}`}>
					<b>
						{wo.workOrderCode}
					</b>
				</span>
				<span className={`${styles['report-type-row__small-text']}`}>
					{wo.jobTitle}
				</span>
				{
					wo.crewTypeColor && wo.crewTypeName &&
					<span className={`${styles['report-type-row__small-text']}`}>
						<LabelWithColor className="bold" color={wo.crewTypeColor} text={wo.crewTypeName as string} />
					</span>
				}
				<span className={`${styles['report-type-row__small-text']}`}>
					{TimeUtils.formatDate(wo.dueDate, TimeFormat.DATE_ONLY_SLASHES)}
				</span>
			</span>
		);
	}, []);

	const numberOfSelected = React.useMemo(() => {
		return Object.values(workOrderWatchedValues).reduce((acc, value) => {
			if (value) {
				return acc + 1;
			}
			return acc;
		}, 0);
	}, [workOrderWatchedValues]);

	const onFilterTextChange = React.useCallback((_filterText: string) => {
		setFilterText(_filterText);
	}, []);

	const filter = React.useCallback((option: W_WorkOrder_FindAvailableForAttachPreviousFRTs_VM_Row) => {
		if (!filterText) {
			return true;
		}

		const searchInputs = (filterText || '').replace(/\s\s+/g, ' ').toLowerCase().split(' ');
		const isFiltered = searchInputs.every((_searchInput: string) => {
			return filterBy.some((_field: string) => {
				const value = option?.[_field] || '';
				return value.toLowerCase().includes(_searchInput);
			});
		});
		return isFiltered;
	}, [filterText]);

	const filteredWorkOrders = React.useMemo(() => {
		return availableWorkOrders.filter(filter);
	}, [availableWorkOrders, filter]);

	const renderWorkOrderCheckbox = React.useCallback((wo: W_WorkOrder_FindAvailableForAttachPreviousFRTs_VM_Row) => {
		return (
			wo.isAlreadyAttached ?
				<Tooltip message="Field Report Type already added to this Work Order" placement="left">
					<div key={wo.id}>
						<Checkbox
							isChecked={true}
							isDisabled={true}
							label={renderWorkOrderDetails(wo)}
						/>
					</div>
				</Tooltip>
				:
				<div key={wo.id}>
					<RHFCheckbox
						control={control}
						defaultValue={false}
						label={renderWorkOrderDetails(wo)}
						name={`workOrders.${wo.id}`}
					/>
				</div>
		);
	}, [control, renderWorkOrderDetails]);

	const selectAll = React.useCallback(() => {
		const currentValues = getValues('workOrders');
		setValue(
			'workOrders',
			{
				...currentValues,
				...filteredWorkOrders.reduce(
					(acc, wo) => {
						if (wo.isAlreadyAttached) {
							return { ...acc };
						}
						return { ...acc, [wo.id]: true };
					},
					{}
				),
			}
		);
	}, [filteredWorkOrders, getValues, setValue]);

	const clearAll = React.useCallback(() => {
		const currentValues = getValues('workOrders');
		setValue(
			'workOrders',
			{
				...currentValues,
				...filteredWorkOrders.reduce(
					(acc, wo) => {
						if (wo.isAlreadyAttached) {
							return { ...acc };
						}
						return { ...acc, [wo.id]: false };
					},
					{}
				),
			}
		);
	}, [filteredWorkOrders, getValues, setValue]);

	const discardAndExit = React.useCallback(() => {
		setShowDiscardModalData(false);
		closeModal();
	}, [closeModal]);

	const syncFieldReportTypes = React.useCallback((syncData: FieldReportSyncVMs.SyncFieldReportTypes) => {
		const fieldReportTypeStillExists = Object.keys(syncData.fieldReport.typeMap).includes(fieldReportTypeId.toString());
		if (!fieldReportTypeStillExists) {
			discardAndExit();
		}
	}, [discardAndExit, fieldReportTypeId]);

	React.useEffect(() => {
		socket.connection?.subscribe(SocketEvent.V2.BE.FIELD_REPORT.SYNC_FIELD_REPORT_TYPES, syncFieldReportTypes, true);
		return () => { socket.connection?.removeHandler(SocketEvent.V2.BE.FIELD_REPORT.SYNC_FIELD_REPORT_TYPES, syncFieldReportTypes); };
	}, [syncFieldReportTypes]);

	const submit = React.useCallback(async () => {
		const values = getValues();
		const selectedWOs = Object.entries(values.workOrders).reduce<number[]>((acc, [woId, selected]) => {
			if (selected) {
				acc.push(+woId);
			}
			return acc;
		}, []);
		await attachFieldReportTypePDF(fieldReportTypeId, selectedWOs);
		discardAndExit();
	}, [getValues, attachFieldReportTypePDF, fieldReportTypeId, discardAndExit]);

	const promptCancel = React.useCallback(() => {
		if (numberOfSelected) {
			setShowDiscardModalData(true);
		} else {
			discardAndExit();
		}
	}, [discardAndExit, numberOfSelected]);

	return (
		<>
			<CustomModal
				closeModal={promptCancel}
				modalStyle="info"
				showModal={showModal && !showDiscardModalData}
				size="lg"
			>
				<CustomModal.Header
					closeModal={promptCancel}
					title="Select Work Orders"
				/>
				<CustomModal.Body className={`${styles['modal-body']}`}>
					<DevTool control={control} />
					<div className={`${styles['modal-body-row']} ${styles['bold-text']}`}>
						Select Work Order to attach Report Type to:
					</div>
					<div className={`${styles['modal-body-row']} ${styles['light-italic-text']}`}>
						Showing Work Orders from {twoDaysBeforeNowDate} to {twoDaysFromNowDate} from the same Job
					</div>
					<div className={`${styles['modal-body-row']} ${styles['search-input']}`}>
						<SearchInput
							name="searchWorkOrders"
							onChange={onFilterTextChange}
							placeholder="Search"
							value={filterText}
						/>
					</div>
					<div className={`${styles['modal-body-row']} ${styles['light-italic-text']}`}>
						{numberOfSelected} item(s) selected
					</div>
					{
						!!filteredWorkOrders.length ?
							<>
								<div className={`${styles['modal-body-row']} ${styles['multiselect-options__controls']}`}>
									<Button
										label="Select all"
										onClick={selectAll}
										size="regular"
										style="link"
									/>
									<Button
										label="Clear all"
										onClick={clearAll}
										size="regular"
										style="link"
									/>
								</div>
								{
									!!filteredWorkOrders.length &&
									<div className={`${styles['scrollable-container']}`}>
										{filteredWorkOrders.map(renderWorkOrderCheckbox)}
									</div>
								}
							</>
							:
							isLoading
								?
								<div className={styles['report-type-list']}>
									<div className={styles['report-type-list--empty']}>
										<LoadingIndicator color="orange" />
									</div>
								</div>
								:
								<div className={styles['report-type-list']}>
									<div className={styles['report-type-list--empty']}>
										No work orders found.
									</div>
								</div>

					}
				</CustomModal.Body>
				<CustomModal.Footer>
					<Button label="Cancel" onClick={promptCancel} style="secondary" />
					<Button disabled={!numberOfSelected} label="Attach to Selected" onClick={submit} style="primary" type="submit" />
				</CustomModal.Footer>
			</CustomModal>
			<ConfirmationModal
				body="Canceling without saving will discard all changes. Do you want to continue?"
				closeModal={closeDiscardModal}
				closeText="Cancel"
				confirmAction={discardAndExit}
				confirmText="Discard"
				modalStyle="danger"
				showModal={showDiscardModalData}
				title="Discard Changes"
			/>
		</>
	);

};

function mapDispatchToProps() {
	return {
		findAllForAttachPreviousFRs: WorkOrderActions.findAllForAttachPreviousFRs,
		attachFieldReportTypePDF: FieldReportActions.attachFieldReportTypePDF,
	};
}
const connector = connect(null, mapDispatchToProps());
const enhance = compose<React.ComponentClass<OwnProps>>(connector, React.memo);

export default enhance(SelectWorkOrdersModal);
